import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';

interface DatenschutzProps {
  isOpen: boolean;
  onClose: () => void;
}

const Datenschutz: React.FC<DatenschutzProps> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div 
        className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
              {t('datenschutz.title')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label={t('impressum.close')}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <div className="space-y-6 text-gray-700 dark:text-gray-300">
            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.firstcontact.title')}
              </h3>
              <pre className="whitespace-pre-wrap mb-6 font-sans">{t('datenschutz.firstcontact.content')}</pre>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.dataCollection.title')}
              </h3>
              <p className="mb-6">{t('datenschutz.dataCollection.content')}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.storage.title')}
              </h3>
              <p className="mb-6">{t('datenschutz.storage.content')}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.transfer.title')}
              </h3>
              <p className="mb-6">{t('datenschutz.transfer.content')}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.recaptcha.title')}
              </h3>
              <p className="mb-4">{t('datenschutz.recaptcha.content')}</p>
              <p className="mb-4">{t('datenschutz.recaptcha.legalBasis')}</p>
              <p className="mb-4">
                {t('datenschutz.recaptcha.moreInfo')}{' '}
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {t('datenschutz.recaptcha.privacyPolicy')}
                </a>{' '}
                {t('datenschutz.recaptcha.and')}{' '}
                <a 
                  href="https://policies.google.com/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {t('datenschutz.recaptcha.terms')}
                </a>.
              </p>
              <p className="mb-6 italic">{t('datenschutz.recaptcha.notice')}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.cookies.title')}
              </h3>
              <p className="mb-6">{t('datenschutz.cookies.content')}</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.rights.title')}
              </h3>
              <div className="whitespace-pre-line mb-6">
                {t('datenschutz.rights.content').split('\n').map((line, i) => (
                  <p key={i} className={line.startsWith('-') ? 'ml-4' : ''}>
                    {line}
                  </p>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2 text-indigo-600 dark:text-indigo-400">
                {t('datenschutz.contact.title')}
              </h3>
              <p className="whitespace-pre-line">{t('datenschutz.contact.content')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Datenschutz;
